import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import AppShell from '../shell/AppShell';
import { UserContext } from '../../UserContext';
import dataService from '../../services/dataService';
import DiscoveryScreen from '../../features/discovery/DiscoveryScreen';
import UnifiedProfile from './UnifiedProfile';
import UnifiedAllergies from './UnifiedAllergies';
import UnifiedPreferences from './UnifiedPreferences';
import UnifiedFamily from './UnifiedFamily';
import { MyTablePanel } from './UnifiedMyTable';
import UnifiedRewards from './UnifiedRewards';
import { useI18n } from '../../features/i18n';
import '../../features/brand/cwm-tokens.css';

/**
 * /unified/dashboard — branded tabbed shell.
 *
 * Single horizontal pill rail (CSS grid, equal widths on desktop, scrollable row
 * on mobile) + single animated content well below it. Each pill is a framer-motion
 * button with staggered entrance, lift-on-hover, press-shrink, and tone-specific
 * glow. Active panel fades/slides in via AnimatePresence. Embedded sub-screens
 * render flush — nested containers/padding are neutralized via a scoped class.
 */

const ROLE_META = {
  0: { label: 'Diner',            tone: '#1E9BFF' },
  1: { label: 'Admin',            tone: '#B1202A' },
  2: { label: 'Restaurant Owner', tone: '#0F7FE8' },
  3: { label: 'Server',           tone: '#3AA17E' },
};

const PANEL_MOTION = {
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -12 },
  transition: { duration: 0.28, ease: [0.22, 1, 0.36, 1] },
};

// Tone tokens drive the pill's idle + hover + active states.
// idle: soft bg tinted with tone, colored text
// active: solid tone bg with white text and a glowing shadow
const TONES = {
  blue:    { bg: '#EAF4FF', fg: '#0F5FB8', br: '#B8DAF6', active: '#0F7FE8', glow: 'rgba(15,127,232,0.32)'  },
  red:     { bg: '#FFECEC', fg: '#9F1818', br: '#F4B6B6', active: '#B91C1C', glow: 'rgba(185,28,28,0.32)'    },
  orange:  { bg: '#FFEEDD', fg: '#8A4A00', br: '#F2D08A', active: '#E86A2B', glow: 'rgba(232,106,43,0.34)'   },
  green:   { bg: '#E6FBEF', fg: '#0F7A3A', br: '#A6E8C2', active: '#16803C', glow: 'rgba(22,128,60,0.28)'    },
  purple:  { bg: '#F1E8FE', fg: '#5B21B6', br: '#CFBAF0', active: '#7C3AED', glow: 'rgba(124,58,237,0.32)'   },
  indigo:  { bg: '#EEF0FF', fg: '#3730A3', br: '#C7CAF0', active: '#4F46E5', glow: 'rgba(79,70,229,0.32)'    },
};

// Builders return the full tab shapes given a translator `t`. Keeping these
// as builders (not static arrays) means changing the language instantly
// rebuilds the labels on the next render — no extra state to invalidate.
function makeDinerTabs(t) {
  return [
    { id: 'discovery',    label: t('dashboard.tab_discover'),    sub: 'Swipe, list, map',         tone: 'blue',   icon: 'discover' },
    { id: 'edit-profile', label: t('dashboard.tab_profile'),     sub: 'Identity & account',       tone: 'indigo', icon: 'person' },
    { id: 'allergies',    label: t('dashboard.tab_allergies'),   sub: 'Critical protection',      tone: 'red',    icon: 'warning' },
    { id: 'preferences',  label: t('dashboard.tab_preferences'), sub: 'Taste & habits',           tone: 'green',  icon: 'tune' },
    { id: 'family',       label: t('dashboard.tab_family'),      sub: 'Shared profiles',          tone: 'orange', icon: 'people' },
    { id: 'table',        label: t('dashboard.tab_table'),       sub: 'Connections & invites',    tone: 'purple', icon: 'table' },
    { id: 'rewards',      label: t('dashboard.tab_rewards'),     sub: 'Points, milestones',       tone: 'indigo', icon: 'star' },
  ];
}
function makeMerchantTabs(t) {
  return [
    { id: 'discovery',    label: t('dashboard.tab_discover'),   sub: 'Diner-facing browse',      tone: 'blue',   icon: 'discover' },
    { id: 'operations',   label: t('dashboard.tab_operations'), sub: 'Overview, specials, signals', tone: 'purple', icon: 'table', route: '/unified/owner-dashboard' },
    { id: 'menus',        label: t('dashboard.tab_menus'),      sub: 'Edit menus & documents',   tone: 'green',  icon: 'tune',   route: '/unified/menu-manager' },
    { id: 'add',          label: t('dashboard.tab_add_rest'),   sub: 'Create a new listing',    tone: 'orange', icon: 'people', route: '/unified/add-restaurant' },
    { id: 'edit-profile', label: t('dashboard.tab_profile'),    sub: 'Account & team',          tone: 'indigo', icon: 'person' },
  ];
}
function makeServerTabs(t) {
  return [
    { id: 'discovery',    label: t('dashboard.tab_discover'), sub: 'Help guests fast',         tone: 'blue',   icon: 'discover' },
    { id: 'service',      label: t('dashboard.tab_service'),  sub: 'Allergy & specials lookup', tone: 'red',   icon: 'warning', route: '/unified/server-dashboard' },
    { id: 'edit-profile', label: t('dashboard.tab_profile'),  sub: 'Account details',          tone: 'indigo', icon: 'person' },
  ];
}

// Map tab id → i18n key family. Each tab's panel header resolves its
// eyebrow/title/subtitle from dashboard.panel_*_eyebrow|title|sub at
// render time so language switches are live.
const TAB_PANEL_KEY = {
  discovery:      'discovery',
  'edit-profile': 'profile',
  allergies:      'allergies',
  preferences:    'preferences',
  family:         'family',
  table:          'table',
  rewards:        'rewards',
};

export default function UnifiedDashboard() {
  const { userId, isInitialized } = useContext(UserContext);
  const navigate = useNavigate();
  const { t } = useI18n();
  const [userData, setUserData] = useState(null);
  const [activeTab, setActiveTab] = useState('discovery');

  useEffect(() => {
    if (!isInitialized) return;
    if (!userId) { navigate('/unified'); return; }
    let cancelled = false;
    dataService.loadUserData(userId).then((u) => { if (!cancelled) setUserData(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [userId, isInitialized, navigate]);

  const role = userData?.account_type ?? 0;
  // Translated role label (replaces the module-scoped ROLE_META labels).
  // Tones stay hard-coded since they're visual tokens, not strings.
  const roleMeta = useMemo(() => {
    const tone = ROLE_META[role]?.tone || '#1E9BFF';
    const roleKey = role === 1 ? 'dashboard.role_admin'
                  : role === 2 ? 'dashboard.role_owner'
                  : role === 3 ? 'dashboard.role_server'
                  :              'dashboard.role_diner';
    return { label: t(roleKey), tone };
  }, [role, t]);
  const tabs = useMemo(() => {
    if (role === 1 || role === 2) return makeMerchantTabs(t);
    if (role === 3) return makeServerTabs(t);
    return makeDinerTabs(t);
  }, [role, t]);

  const active = tabs.find((x) => x.id === activeTab) || tabs[0];
  // Resolve the panel header strip from i18n. Unknown tabs fall back to
  // the discovery key family so merchants on /operations (which navigates
  // away) don't flash an empty header on the way out.
  const panelKey = TAB_PANEL_KEY[active.id] || 'discovery';
  const meta = {
    eyebrow:  t(`dashboard.panel_${panelKey}_eyebrow`),
    title:    t(`dashboard.panel_${panelKey}_title`),
    subtitle: t(`dashboard.panel_${panelKey}_sub`),
  };

  return (
    <AppShell dense backdrop>
      <style>{embeddedFlushCss}</style>

      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '24px 20px 72px' }}>
        {/* Hero band */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          style={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 28,
            padding: '24px 28px',
            marginBottom: 18,
            background:
              'radial-gradient(circle at 20% 0%, rgba(255,255,255,0.55), transparent 40%), linear-gradient(160deg, #BCE7FF 0%, #5BC2FF 45%, #1E9BFF 100%)',
            boxShadow: '0 20px 48px rgba(13,71,161,0.22)',
            color: '#fff',
            textAlign: 'center',
          }}
        >
          <div aria-hidden="true" style={{
            position: 'absolute', inset: 0, pointerEvents: 'none',
            background: 'radial-gradient(circle, rgba(255,255,255,0.22) 0, rgba(255,255,255,0.22) 1.5px, transparent 1.6px)',
            backgroundSize: '22px 22px', opacity: 0.3,
          }}/>
          <div style={{ position: 'relative' }}>
            {roleMeta && (
              <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'rgba(255,255,255,0.92)', color: roleMeta.tone,
                padding: '5px 14px', borderRadius: 999,
                fontSize: 11, fontWeight: 900, letterSpacing: '0.14em', textTransform: 'uppercase',
                border: '1.5px solid rgba(255,255,255,0.95)',
                marginBottom: 12,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: 999, background: roleMeta.tone }}/>
                {roleMeta.label}
              </div>
            )}
            <h1 style={{ fontSize: 36, fontWeight: 900, lineHeight: 1.05, margin: '0 0 6px', letterSpacing: '-0.02em', textShadow: '0 8px 24px rgba(13,71,161,0.2)' }}>
              {userData ? t('dashboard.welcome', { name: userData.name || 'friend' }) : t('dashboard.welcome_fallback')}
            </h1>
            <p style={{ maxWidth: 620, margin: '0 auto', fontSize: 15, fontWeight: 600, color: 'rgba(255,255,255,0.92)' }}>
              {role === 1 || role === 2
                ? t('dashboard.subtitle_merchant')
                : role === 3
                ? t('dashboard.subtitle_server')
                : t('dashboard.subtitle_diner')}
            </p>
          </div>
        </motion.section>

        {/* Pill rail */}
        <div
          className="cwm-dash-pill-wrap"
          style={{
            background: 'rgba(255,255,255,0.88)',
            backdropFilter: 'blur(14px) saturate(1.08)',
            WebkitBackdropFilter: 'blur(14px) saturate(1.08)',
            border: '1px solid rgba(255,255,255,0.95)',
            borderRadius: 999,
            padding: 8,
            boxShadow: '0 14px 32px rgba(13,71,161,0.12), inset 0 1px 0 rgba(255,255,255,0.92)',
            marginBottom: 18,
          }}
        >
          <div className="cwm-dash-pillrow">
            {tabs.map((t, i) => (
              <Pill
                key={t.id}
                tab={t}
                index={i}
                active={t.id === activeTab && !t.route}
                onClick={() => {
                  if (t.route) { navigate(t.route); return; }
                  setActiveTab(t.id);
                }}
              />
            ))}
          </div>
        </div>

        {/* Content well */}
        <div style={{
          background: 'linear-gradient(180deg, #fff, #F6FAFF)',
          border: '1px solid rgba(255,255,255,0.98)',
          borderRadius: 28,
          overflow: 'hidden',
          boxShadow: '0 20px 46px rgba(13,71,161,0.10), inset 0 1px 0 rgba(255,255,255,0.96)',
        }}>
          {/* Panel header */}
          <div style={{
            padding: '22px 26px 18px',
            borderBottom: '1px solid rgba(15,127,232,0.08)',
            background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(245,250,255,0.6))',
          }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              background: '#F7FBFF', color: '#0F7FE8',
              padding: '5px 12px', borderRadius: 999,
              fontSize: 11, fontWeight: 900, letterSpacing: '0.12em', textTransform: 'uppercase',
              border: '1px solid rgba(15,127,232,0.12)',
              marginBottom: 10,
            }}>
              {meta.eyebrow}
            </div>
            <h2 style={{ fontSize: 22, fontWeight: 900, margin: '0 0 4px', letterSpacing: '-0.01em', color: 'var(--cwm-ink, #0A2540)' }}>
              {meta.title}
            </h2>
            <p style={{ fontSize: 14, color: 'var(--cwm-gray-500, #6B7280)', margin: 0, fontWeight: 500 }}>
              {meta.subtitle}
            </p>
          </div>

          {/* Embedded panel */}
          <div className="cwm-dash-panel-body">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={PANEL_MOTION.initial}
                animate={PANEL_MOTION.animate}
                exit={PANEL_MOTION.exit}
                transition={PANEL_MOTION.transition}
              >
                {activeTab === 'discovery'    && <DiscoveryScreen userId={userId} withBackdrop={false} embedded />}
                {activeTab === 'edit-profile' && <UnifiedProfile userId={userId} />}
                {activeTab === 'allergies'    && <UnifiedAllergies userId={userId} />}
                {activeTab === 'preferences'  && <UnifiedPreferences userId={userId} />}
                {activeTab === 'family'       && <UnifiedFamily userId={userId} />}
                {activeTab === 'table'        && <MyTablePanel userId={userId} />}
                {activeTab === 'rewards'      && <UnifiedRewards userData={userData} embedded />}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </AppShell>
  );
}

function Pill({ tab, index, active, onClick }) {
  const tone = TONES[tab.tone] || TONES.blue;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.34, delay: index * 0.04, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -3, scale: 1.015 }}
      whileTap={{ scale: 0.98 }}
      style={{
        height: 54,
        padding: '0 16px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        cursor: 'pointer',
        borderRadius: 999,
        fontFamily: 'var(--cwm-font, "Nunito", system-ui, sans-serif)',
        border: `1.5px solid ${active ? tone.active : tone.br}`,
        background: active
          ? `linear-gradient(180deg, ${tone.active}, ${tone.active})`
          : `linear-gradient(180deg, #fff, ${tone.bg})`,
        color: active ? '#fff' : tone.fg,
        boxShadow: active
          ? `0 10px 22px ${tone.glow}, inset 0 1px 0 rgba(255,255,255,0.22)`
          : `0 4px 12px ${tone.glow.replace(/0\.\d+/, '0.14')}, inset 0 1px 0 rgba(255,255,255,0.92)`,
        transition: 'background 180ms ease, color 180ms ease, border-color 180ms ease',
      }}
    >
      <PillIcon name={tab.icon} color={active ? '#fff' : tone.fg} active={active} />
      <span style={{
        fontSize: 13.5,
        fontWeight: 900,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {tab.label}
      </span>
    </motion.button>
  );
}

function PillIcon({ name, color }) {
  const paths = {
    discover: 'M12 2a10 10 0 100 20 10 10 0 000-20zm3.5 6.5L14 14l-5.5 1.5L10 10z',
    person:   'M12 12a4 4 0 100-8 4 4 0 000 8zM4 21a8 8 0 0116 0',
    warning:  'M12 2l10 18H2zM12 9v5M12 17h.01',
    tune:     'M4 6h10M4 12h6M4 18h14M16 4v4M12 10v4M18 16v4',
    people:   'M9 11a3 3 0 100-6 3 3 0 000 6zM17 12a2.5 2.5 0 100-5 2.5 2.5 0 000 5zM3 19c0-3 3-5 6-5s6 2 6 5M16 19c0-2 1.5-4 4-4',
    table:    'M3 10h18M3 14h18M6 10v8M18 10v8M4 6h16v4H4z',
    star:     'M12 2l3 7 7 1-5 5 1 7-6-3-6 3 1-7-5-5 7-1z',
  };
  const d = paths[name];
  if (!d) return null;
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d={d} />
    </svg>
  );
}

// Flush the embedded recovery/MUI screens. They were built as standalone full-page
// routes and ship their own Container/Paper padding that makes the dashboard feel
// like "an old window placed in a container." This neutralizes the wrappers so the
// embedded content sits flush with the panel header.
const embeddedFlushCss = `
.cwm-dash-pill-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.cwm-dash-pill-wrap::-webkit-scrollbar { display: none; }
.cwm-dash-pillrow {
  display: flex;
  flex-wrap: nowrap;
  gap: 8px;
  min-width: max-content;
}
.cwm-dash-pillrow > button {
  flex: 0 0 auto !important;
  min-width: 140px;
}
@media (min-width: 1040px) {
  .cwm-dash-pillrow { min-width: 100%; }
  .cwm-dash-pillrow > button { flex: 1 1 0 !important; min-width: 0; }
}
.cwm-dash-panel-body {
  padding: 6px;
  background: linear-gradient(180deg, #fff, #F6FAFF);
}
.cwm-dash-panel-body > div > .MuiContainer-root {
  max-width: none !important;
  padding: 16px !important;
}
.cwm-dash-panel-body .MuiPaper-root {
  border-radius: 20px !important;
  box-shadow: 0 8px 24px rgba(10,37,64,0.06) !important;
}
/* Kill the nested back-button bars inside embedded screens — the dashboard
   already communicates location via the tab rail + panel header. */
.cwm-dash-panel-body .MuiAppBar-root,
.cwm-dash-panel-body [class*="BackButton"],
.cwm-dash-panel-body button[aria-label="back"],
.cwm-dash-panel-body button[aria-label="Back"] {
  display: none !important;
}
`;
